import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { StarRating } from "./StarRating";
import { useCSATSubmit } from "@/hooks/useCSATSubmit";
import { cn } from "@/lib/utils";

const getPageInfo = (pathname: string): { id: string; name: string } => {
  // Tratar rotas dinâmicas primeiro
  if (
    (pathname.startsWith('/coleta-sob-demanda/') || pathname.startsWith('/coletasobdemanda/')) &&
    pathname !== '/coleta-sob-demanda' &&
    pathname !== '/coletasobdemanda'
  ) {
    return { id: 'coletaDetalhes', name: 'Detalhes da Coleta' };
  }
  if (pathname.startsWith('/perguntasfrequentes/')) {
    return { id: 'perguntasFrequentes', name: 'Perguntas Frequentes' };
  }

  const routeMap: Record<string, { id: string; name: string }> = {
    '/': { id: 'dashboard', name: 'Dashboard' },
    '/coleta-sob-demanda': { id: 'coletasSobDemanda', name: 'Coletas Sob Demanda' },
    '/coletasobdemanda': { id: 'coletasSobDemanda', name: 'Coletas Sob Demanda' },
    '/coletasrecorrentes': { id: 'coletasRecorrentes', name: 'Coletas Recorrentes' },
    '/financeiro': { id: 'financeiro', name: 'Financeiro' },
    '/users-permissions': { id: 'usersPermissions', name: 'Usuários e Permissões' },
    '/csat': { id: 'csat', name: 'CSAT' },
    // Novas páginas
    '/dadosimpacto': { id: 'dadosImpacto', name: 'Dados de Impacto' },
    '/configuracoes': { id: 'configuracoes', name: 'Configurações' },
    // Mitigação temporária: Notificações oculto até correção definitiva
    // '/notificacoes': { id: 'notificacoes', name: 'Notificações' },
    '/perguntasfrequentes': { id: 'perguntasFrequentes', name: 'Perguntas Frequentes' },
    '/suporte': { id: 'suporte', name: 'Suporte' },
    '/termosdeuso': { id: 'termosDeUso', name: 'Termos de Uso' },
    '/status-metrics': { id: 'statusMetrics', name: 'Status Metrics' },
  };

  return routeMap[pathname] || { 
    id: pathname.replace('/', '') || 'home', 
    name: pathname.replace('/', '') || 'página' 
  };
};

interface CSATPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CSATPopup = ({ isOpen, onClose }: CSATPopupProps) => {
  const location = useLocation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const submitMutation = useCSATSubmit();

  const pageInfo = getPageInfo(location.pathname);
  const requiresComment = rating > 0 && rating < 3;
  const isCommentValid = !requiresComment || comment.trim().length >= 20;
  const canSubmit = rating > 0 && isCommentValid;

  useEffect(() => {
    if (!isOpen) {
      setRating(0);
      setComment("");
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    await submitMutation.mutateAsync({
      page_id: pageInfo.id,
      page_name: pageInfo.name,
      page_path: location.pathname,
      rating,
      comment: comment.trim() || undefined,
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-full max-w-[360px] px-4 sm:px-0">
      <Card className="relative shadow-lg border-border bg-card">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 p-1 rounded-sm hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Fechar"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        <div className="p-4 space-y-3">
          <div className="pr-5">
            <h3 className="text-base font-semibold text-foreground">
              Como você avalia o {pageInfo.name}?
            </h3>
          </div>

          <div className="flex justify-center py-1">
            <StarRating
              value={rating}
              onChange={setRating}
              disabled={submitMutation.isPending}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="csat-comment" className="text-sm font-medium text-foreground">
                Comentário {requiresComment && <span className="text-destructive">*</span>}
              </label>
              {comment.length > 0 && (
                <span className={cn(
                  "text-xs",
                  requiresComment && comment.trim().length < 20 
                    ? "text-destructive" 
                    : "text-muted-foreground"
                )}>
                  {comment.trim().length}/20
                </span>
              )}
            </div>
            <Textarea
              id="csat-comment"
              placeholder={
                requiresComment
                  ? "Explique o motivo da sua nota (mínimo 20 caracteres)"
                  : "Conte-nos mais sobre sua experiência (opcional)"
              }
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitMutation.isPending}
              className="resize-none min-h-[70px] text-sm"
              aria-required={requiresComment}
            />
            {requiresComment && comment.trim().length < 20 && comment.trim().length > 0 && (
              <p className="text-xs text-destructive">
                Explique o motivo da sua nota em pelo menos 20 caracteres.
              </p>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={submitMutation.isPending}
              className="flex-1"
              size="sm"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitMutation.isPending}
              className="flex-1"
              size="sm"
            >
              {submitMutation.isPending ? "Enviando..." : "Enviar"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
